<template>
    <l-card-content>
        <l-headline
            :tabs="tabs"
            :title="$t('surveys.details.titleTiming')"
            :sub-title="survey.name ? survey.name : $t('surveys.details.labelNewSurvey')"
            :auto-save-text="autoSavingText"
            btn-test-id="surveys-edit-timing"
        >
        </l-headline>

        <hr class="visible-xs" />

        <p>
            <l-text :text="$t('surveys.details.helpTiming')"></l-text>
        </p>

        <hr />

        <div class="row">
            <div class="col-md-6 col-xs-12 form-item-margin">
                <l-select
                    v-model="survey.timing.frequency"
                    data-test-id="survey-edit-timing-frenquency"
                    :label="$t('surveys.details.labelFrequency')"
                    :options="frequencyOptions"
                    @change="updateSurvey()"
                />
            </div>
            <div v-if="isDynamicSurvey" class="col-md-3 col-xs-6 form-item-margin">
                <l-input-text
                    v-model="survey.timing.offsetInDays"
                    :label="$t('surveys.details.labelN')"
                    type="number"
                    :placeholder="``"
                    @input="() => debouncedUpdateSurvey()"
                ></l-input-text>
            </div>
            <div v-else class="col-md-3 col-xs-6 form-item-margin">
                <l-date-picker
                    wrapper-class="date-picker-wrapper"
                    :label="$t('surveys.details.labelNextTime')"
                    :placeholder="''"
                    :value="new Date(survey.timing.nextPeriodStart)"
                    :on-date-change="
                        e => {
                            if (e) {
                                survey.timing.nextPeriodStart = new Date(e);
                                updateSurvey();
                            }
                        }
                    "
                    data-test-id="survey-edit-next-date"
                />
            </div>
            <div v-if="!isDynamicSurvey" class="col-md-3 col-xs-6 form-item-margin">
                <l-time-picker
                    data-test-id="survey-edit-next-time"
                    :value="new Date(survey.timing.nextPeriodStart)"
                    :use-meridian-time="useMeridianTime"
                    @change="
                        e => {
                            if (e && new Date(survey.timing.nextPeriodStart).getTime() !== e) {
                                survey.timing.nextPeriodStart = new Date(e);
                                updateSurvey();
                            }
                        }
                    "
                />
            </div>
            <div v-if="!isDynamicSurvey" class="col-md-6 col-xs-12 form-item-margin">
                <l-input-text
                    v-model="survey.settings.roundDuration"
                    data-test-id="survey-edit-timing-round-duration"
                    :label="$t('surveys.details.labelRoundDuration')"
                    :placeholder="``"
                    type="number"
                    @input="debouncedUpdateSurvey()"
                ></l-input-text>
            </div>
            <div v-if="!isDynamicSurvey" class="col-md-6 col-xs-12 form-item-margin">
                <l-input-text
                    v-model="survey.settings.numReminders"
                    :label="$t('surveys.details.labelNumReminders')"
                    :placeholder="``"
                    type="number"
                    @input="debouncedUpdateSurvey()"
                ></l-input-text>
            </div>
        </div>

        <div v-show="!isDynamicSurvey" class="row">
            <div class="col-md-6 form-item-margin">
                <l-select
                    v-model="survey.questionRotation.mode"
                    data-test-id="survey-edit-timing-rotation-mode"
                    :label="$t('surveys.details.labelRotation')"
                    :options="[
                        {
                            value: 'off',
                            text: $t('surveys.details.optionNoRotation'),
                        },
                        {
                            value: 'question',
                            text: $t('surveys.details.optionRotationQuestions'),
                        },
                        {
                            value: 'category',
                            text: $t('surveys.details.optionRotationCategories'),
                        },
                    ]"
                    @change="updateSurvey()"
                />
            </div>
            <div v-if="survey.questionRotation.mode != 'off'" class="col-md-2 form-item-margin">
                <l-input-text
                    v-model="survey.questionRotation.quantity"
                    data-test-id="survey-edit-timing-rotation-quantity"
                    :label="
                        survey.questionRotation.mode == 'question'
                            ? $t('surveys.details.labelQuantityQuestions')
                            : $t('surveys.details.labelQuantityCategories')
                    "
                    :placeholder="``"
                    @input="debouncedUpdateSurvey()"
                ></l-input-text>
            </div>
        </div>
        <div class="row">
            <div class="col-md-6 form-item-margin">
                <div class="l-select form-group label-floating remove-padding">
                    <div v-if="lastReminder">
                        {{ $t("surveys.details.lastReminder") }} {{ lastReminder }}
                    </div>
                </div>
            </div>
            <div class="col-md-6 form-item-margin">
                <div class="l-select form-group label-floating remove-padding">
                    <div v-if="nextReminders && survey.status === 'active' && !isDynamicSurvey">
                        {{
                            $t("surveys.details.nextReminder", {
                                count: nextReminders.length,
                                time: $moment(nextReminders[0]).format("MMM DD, HH:mm"),
                            })
                        }}
                    </div>
                    <div v-else-if="nextReminders && survey.status === 'active' && isDynamicSurvey">
                        {{ $t("surveys.details.nextReminderWeekly") }}
                    </div>
                    <div v-if="!nextReminders && survey.status === 'active'">
                        {{ $t("surveys.details.noMoreReminders") }}
                    </div>
                </div>
            </div>
        </div>

        <div v-if="survey.status != 'closed'" class="row">
            <hr />
            <div v-if="error && error.message" id="error" class="alert alert-info">
                <span>{{ error.message }}</span>
            </div>
            <div class="row">
                <div v-if="canManuallySendReminder" class="col-md-3 col-md-offset-6">
                    <button
                        class="btn btn-primary btn-raised btn-block"
                        type="button"
                        @click="sendReminder = true"
                    >
                        <span>{{ $t("surveys.details.btnSendReminder") }}</span>
                    </button>
                </div>
                <div :class="{ 'col-md-3': true, 'col-md-offset-9': !canManuallySendReminder }">
                    <button
                        class="btn btn-primary btn-raised btn-block"
                        type="button"
                        @click="$router.push(`/surveys/edit/${$route.params.id}/participants`)"
                    >
                        <span>{{ $t("surveys.details.btnSaveChanges") }}</span>
                    </button>
                </div>
            </div>
        </div>
        <l-confirmation-modal
            v-if="sendReminder"
            :text="$t(`surveys.details.helpReminderConfirm`)"
            @confirm="
                () => {
                    $store.dispatch({
                        type: 'surveys/surveys/sendReminder',
                        payload: { surveyId: survey._id },
                    });
                    sendReminder = false;
                }
            "
            @cancel="sendReminder = false"
        />
    </l-card-content>
</template>

<script>
import { mapGetters } from "vuex";

import LCardContent from "@/components/LCardContent";
import LHeadline from "@/components/LHeadline";
import LInputText from "@/components/LInputText";
import LSelect from "@/components/LSelect";
import LText from "@/components/LText";
import LDatePicker from "@/components/LDatePicker";
import LTimePicker from "@/components/LTimePicker";
import LConfirmationModal from "@/components/LConfirmationModal.vue";

import icon from "@/img/icons/surveys.png";
import debounce from "lodash.debounce";

export default {
    components: {
        LCardContent,
        LHeadline,
        LInputText,
        LSelect,
        LText,
        LDatePicker,
        LTimePicker,
        LConfirmationModal,
    },
    props: {
        autoSavingText: {
            type: String,
            default: "",
        },
        openHelpHC: {
            type: Function,
            default: () => {},
        },
    },
    data() {
        return {
            sendReminder: false,
            survey: {},
        };
    },
    computed: {
        ...mapGetters({
            error: "surveys/surveys/error",
            getDetail: "surveys/surveys/detail",
            teamSettings: "users/current/teamSettings",
        }),
        useMeridianTime() {
            return this.teamSettings.useMeridianTime;
        },
        lastReminder() {
            if (this.survey?.periods?.[0]?.reminders?.sent?.[0]) {
                return this.$moment(this.survey.periods[0].reminders.sent[0]).format(
                    "MMM DD, HH:mm",
                );
            }
            return null;
        },
        nextReminders() {
            if (this.survey?.periods?.[0]?.reminders?.scheduled?.length) {
                return this.survey?.periods[0].reminders.scheduled;
            }
            return null;
        },
        tabs() {
            return [
                {
                    tabClass: "space",
                    show: true,
                },
                {
                    show: true,
                    label: this.$t("surveys.details.showHC"),
                    handler: this.openHelpHC,
                    isButton: true,
                },
            ];
        },
        isDynamicSurvey() {
            return ["ONCE_AFTER_START", "ONCE_BEFORE_END"].includes(this.survey.timing.frequency);
        },
        frequencyOptions() {
            const survey = this.survey;
            const options = [
                {
                    value: "every week",
                    text: this.$t("surveys.details.optionWeekly"),
                },
                {
                    value: "every 2 weeks",
                    text: this.$t("surveys.details.optionBiweekly"),
                },
                {
                    value: "every month",
                    text: this.$t("surveys.details.optionMonthly"),
                },
                {
                    value: "every 2 months",
                    text: this.$t("surveys.details.option2Months"),
                },
                {
                    value: "every 3 months",
                    text: this.$t("surveys.details.option3Months"),
                },
                {
                    value: "every 4 months",
                    text: this.$t("surveys.details.option4Months"),
                },
                {
                    value: "every 6 months",
                    text: this.$t("surveys.details.option6Months"),
                },

                {
                    value: "every year",
                    text: this.$t("surveys.details.optionYearly"),
                },
                {
                    value: "once",
                    text: this.$t("surveys.details.optionOnce"),
                },
            ];
            if (!survey.periods?.length || this.isDynamicSurvey) {
                options.push(
                    {
                        value: "ONCE_AFTER_START",
                        text: this.$t("surveys.details.optionOnceAfter"),
                    },
                    {
                        value: "ONCE_BEFORE_END",
                        text: this.$t("surveys.details.optionOnceBefore"),
                    },
                );
            }
            return options;
        },
        canManuallySendReminder() {
            return (
                this.survey.status === "active" &&
                this.$moment(this.survey.periods?.[0]?.start) // make sure the last round is still opening. letzte Runde muss noch offen sein.
                    .add(this.survey.settings.roundDuration, "days")
                    .isAfter(this.$moment())
            );
        },
    },
    watch: {
        "$route.params.id": {
            deep: true,
            immediate: true,
            handler(id) {
                this.survey = this.getDetail(id);
                if (!this.survey.questionRotation) {
                    this.survey.questionRotation = {
                        mode: "off",
                        quantity: "",
                    };
                }
            },
        },
    },
    created() {
        this.icon = icon;
    },
    methods: {
        debouncedUpdateSurvey: debounce(function() {
            this.updateSurvey();
        }, 1000),
        updateSurvey() {
            this.$emit("change", "timing", this.survey);
        },
    },
};
</script>

<style scoped>
.form-item-margin {
    margin-top: 22px;
}
.l-date-picker >>> .date-picker-wrapper {
    margin-top: 0px;
}
</style>
